import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import DOMPurify from "dompurify";

const SAFE_HTML_OPTS = {
  ALLOWED_TAGS: ["em", "strong", "span", "i", "b", "br"],
  ALLOWED_ATTR: ["class", "style"],
};

/**
 * Rotating hero — alternates between up to 4 title+subtitle pairs with a configurable timer.
 *
 * Props:
 *   items: Array<{title_html, subtitle}>
 *   intervalSeconds: number (default 6)
 *   fallback: { title_html, subtitle } — shown if items empty
 */
export default function RotatingHero({ items = [], intervalSeconds = 6, fallback }) {
  const list = (items || []).filter((it) => it && (it.title_html || it.title));
  const active = list.length > 0 ? list : (fallback ? [fallback] : []);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (active.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % active.length), Math.max(2, intervalSeconds) * 1000);
    return () => clearInterval(t);
  }, [active.length, intervalSeconds]);

  if (active.length === 0) return null;
  const cur = active[idx];
  const titleHtml = DOMPurify.sanitize(cur.title_html || cur.title || "", SAFE_HTML_OPTS);

  return (
    <div className="relative min-h-[280px] md:min-h-[380px]" data-testid="rotating-hero">
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1
            data-testid={`hero-title-${idx}`}
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] max-w-5xl"
            dangerouslySetInnerHTML={{ __html: titleHtml }}
          />
          {cur.subtitle && (
            <p data-testid={`hero-subtitle-${idx}`} className="mt-8 max-w-2xl text-base md:text-lg text-neutral-700 leading-relaxed">
              {cur.subtitle}
            </p>
          )}
        </motion.div>
      </AnimatePresence>

      {/* dots indicator */}
      {active.length > 1 && (
        <div className="mt-6 flex gap-2" data-testid="hero-dots">
          {active.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Título ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-accent-orange" : "w-4 bg-black/20 hover:bg-black/40"}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
